import { useCallback } from 'react'

import {
    getCurrentWindowUrl,
    getDocumentReferrer,
    getCardIsDownsell,
} from '@mediaingenuity/totallymoney.cardloanutils'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'

import {
    AcquisitionJourneyBaseData,
    AcquisitionJourneyCardsTableClickoutEvent,
    AcquisitionJourneyCardsTableClickoutEventBasinPayload,
    AcquisitionJourneyCardsTableViewEvent,
    AcquisitionJourneyCardsTableViewEventBasinPayload,
} from '@appTypes/AcquisitionEvents.interface'
import { BasinEvents } from '@appTypes/BasinEvents.interface'
import {
    CreditCardProductCategory,
    ScoredCreditCardProduct,
} from '@appTypes/EligibilityApi.interface'

import { formatFeatureFlagsToExperimentIdString } from '@utils/formatFeatureFlagsToExperimentIdString'
import { getAppConfig } from '@utils/getAppConfig'
import { getMarketingTrackingData } from '@utils/getMarketingTrackingData'

import useAppQueryState from './useAppQueryState'
import { useBasin } from './useBasin'
import useFeatureFlags from './useFeatureFlags'
import useGoogleTagManager from './useGoogleTagManager'
import { getFeatureFlagsAsArray } from './utils/getFeatureFlagsAsArray'

type AppPromotionTypes = 'creditCardsTable' | 'improveEligibilityPromotion'

function getLatestStepUrl(): string {
    const url = new URL(window.location.href)
    url.searchParams.set('step', 'cardCreditLimit')
    return url.toString()
}

const useLogEvents = () => {
    const appConfig = getAppConfig()
    const { query } = useRouter()
    const { send } = useBasin()
    const { appQueryState } = useAppQueryState()
    const gtmSend = useGoogleTagManager()
    const { featureFlags } = useFeatureFlags()

    const trackingData = getMarketingTrackingData(appQueryState)
    const formattedFeatureFlagsExperimentIdString =
        formatFeatureFlagsToExperimentIdString(featureFlags)

    const variations = getFeatureFlagsAsArray(featureFlags)

    const acquisitionGtmBaseData: Omit<
        AcquisitionJourneyBaseData,
        'event_ts' | 'event_details' | 'meta'
    > = {
        brand: 'totallymoney',
        category: 'cc',
        cookie_consents: Cookies.get('cookieyes-consent'),
        event: 'data_custom_event',
        session_id: Cookies.get('BasinSessionId'),
    }

    const whitelabel = appConfig.NAME_WHITELABEL

    const logAcquisitionPageFormOpenEvent = useCallback(
        (page: 'considerationPage' | 'hybridPage') => {
            const payload = {
                ...acquisitionGtmBaseData,
                event_details: {
                    journey:
                        page === 'considerationPage'
                            ? ('Credit Cards Signup' as const)
                            : ('Credit Cards Signup - Hybrid' as const),
                    step: page,
                    step_url: window.location.origin + window.location.pathname,
                    experiment_ids: formattedFeatureFlagsExperimentIdString,
                },
                event_name: 'form_open' as const,
                event_ts: new Date().toISOString(),
                meta: {
                    app_id: query?.app_id,
                    gclid: query?.gclid,
                    msclkid: query?.msclkid,
                    utm_message_id: query?.utm_message_id,
                    utm_source: query?.utm_source,
                    utm_medium: query?.utm_medium,
                    utm_campaign: query?.utm_campaign,
                    utm_campaign_id: query?.utm_campaign_id,
                    utm_content: query?.utm_content,
                    utm_term: query?.utm_term,
                    form_landing_url: getCurrentWindowUrl(),
                    referral_url: getDocumentReferrer(),
                },
            }

            gtmSend(payload)

            send('form_open', {
                ...payload,
                user_id: Cookies.get('BasinPermanentId'),
            })
        },
        [query, gtmSend, send]
    )

    const logAcquisitionPageFormStartEvent = useCallback(() => {
        const payload = {
            ...acquisitionGtmBaseData,
            event_details: {
                journey: 'Credit Cards Signup - Hybrid' as const,
                step: 'cardCreditLimit',
                step_url: window.location.origin + window.location.pathname,
                latest_step_url: getLatestStepUrl(),
                experiment_ids: formattedFeatureFlagsExperimentIdString,
            },
            event_name: 'form_start' as const,
            event_ts: new Date().toISOString(),
            error: undefined,
            form_data: undefined,
            meta: {
                app_id: query?.app_id,
                gclid: query?.gclid,
                msclkid: query?.msclkid,
                utm_message_id: query?.utm_message_id,
                utm_source: query?.utm_source,
                utm_medium: query?.utm_medium,
                utm_campaign: query?.utm_campaign,
                utm_campaign_id: query?.utm_campaign_id,
                utm_content: query?.utm_content,
                utm_term: query?.utm_term,
                form_landing_url: getCurrentWindowUrl(),
                referral_url: getDocumentReferrer(),
            },
            quote: {
                quote_number: 1,
            },
        }

        gtmSend(payload)

        send('form_start', {
            ...payload,
            user_id: Cookies.get('BasinPermanentId'),
        })
    }, [query, gtmSend, send])

    // Customer returns to cards table after viewing acquisition page and going through the registration process.
    const logAcquisitionJourneyCardsTableViewEvent = useCallback(
        ({
            basinPayload,
            dataLayerPayload,
        }: {
            basinPayload: AcquisitionJourneyCardsTableViewEventBasinPayload
            dataLayerPayload: AcquisitionJourneyCardsTableViewEvent
        }) => {
            const basePayload = {
                event_details: {
                    ...basinPayload.event_details,
                    experiment_ids: `${formattedFeatureFlagsExperimentIdString}${basinPayload?.event_details?.experiment_ids ? `,${basinPayload?.event_details?.experiment_ids}` : ''}`,
                },
            }

            gtmSend({
                ...dataLayerPayload,
                ...basePayload,
            })

            send(basinPayload.event_name, {
                ...basinPayload,
                ...basePayload,
            })
        },
        [gtmSend, send]
    )

    const logAcquisitionJourneyCardsTableClickoutEvent = useCallback(
        ({
            basinPayload,
            dataLayerPayload,
        }: {
            basinPayload: AcquisitionJourneyCardsTableClickoutEventBasinPayload
            dataLayerPayload: AcquisitionJourneyCardsTableClickoutEvent
        }) => {
            const basePayload = {
                event_details: {
                    ...basinPayload.event_details,
                    experiment_ids: `${formattedFeatureFlagsExperimentIdString}${basinPayload?.event_details?.experiment_ids ? `,${basinPayload?.event_details?.experiment_ids}` : ''}`,
                },
            }

            gtmSend({
                ...dataLayerPayload,
                ...basePayload,
            })

            send('form_clickout', {
                ...basinPayload,
                ...basePayload,
            })
        },
        [gtmSend, send]
    )

    const logUnscoredPagesFormOpenEvent = useCallback(() => {
        const payload = {
            ...acquisitionGtmBaseData,
            event_details: {
                journey: 'Credit Cards Signup - Unscored' as const,
                step: 'Unscored Page',
                step_url: window.location.origin + window.location.pathname,
                experiment_ids: formattedFeatureFlagsExperimentIdString,
            },
            event_name: 'form_open' as const,
            event_ts: new Date().toISOString(),
            error: undefined,
            form_data: undefined,
            meta: {
                app_id: query?.app_id,
                gclid: query?.gclid,
                msclkid: query?.msclkid,
                utm_message_id: query?.utm_message_id,
                utm_source: query?.utm_source,
                utm_medium: query?.utm_medium,
                utm_campaign: query?.utm_campaign,
                utm_campaign_id: query?.utm_campaign_id,
                utm_content: query?.utm_content,
                utm_term: query?.utm_term,
                form_landing_url: getCurrentWindowUrl(),
                referral_url: getDocumentReferrer(),
            },
        }

        gtmSend(payload)

        send('form_open', {
            ...payload,
            user_id: Cookies.get('BasinPermanentId'),
        })
    }, [query, gtmSend, send])

    const logVisitEvent = useCallback(() => {
        const eventData = {
            referrer: getDocumentReferrer(),
            url: document.URL,
            useragent: navigator.userAgent,
            client: whitelabel,
            partnerName: whitelabel,

            customerId: appQueryState.customerId,
            customerType: appQueryState.customerType,
            isKnownCustomer: appQueryState.isKnownCustomer,
            adgroup: appQueryState.adgroup,
            visitOrigin: appQueryState.visitOrigin,

            sendId: trackingData.sendId,
            mediasource: trackingData.m,
            campaign: trackingData.cam,
            keyword: trackingData.k,
            googleClickId: trackingData.gclid,
            variation: variations.join(','),
        }

        send('Visit', eventData)
    }, [send, appQueryState, trackingData, whitelabel])

    const logPageViewEvent = useCallback(
        ({ category }: { category: string }) => {
            const eventData = {
                category,
                visitOrigin: appQueryState.visitOrigin,
                url: document.URL,
                partnername: whitelabel,
                client: 'web',
                customerId: appQueryState.customerId,
                scoreId: appQueryState.scoreId,
            }
            send('PageView', eventData)
        },
        [send, appQueryState]
    )

    const logCheckEligibilityEvent = useCallback(
        ({
            trigger,
            category,
            productCode,
            rowNumber,
        }: {
            category: string
            productCode?: string
            rowNumber?: number
            trigger: string
        }) => {
            send('FormOpen', {
                trigger,
                category,
                productCode,
                row: rowNumber,
            })
        },
        [send]
    )

    const logNavigateToCardPageEvent = useCallback(
        ({
            rowNumber,
            productCode,
            trigger,
            isNewOffer,
        }: {
            isNewOffer: boolean
            productCode: string
            rowNumber: number
            trigger: string
        }) => {
            gtmSend({
                event: 'show_details',
                trigger,
            })

            send('RowExpand', {
                row: rowNumber,
                productcode: productCode,
                isNewOffer,
            })
        },
        [send]
    )

    const logCardApplyEvent = useCallback(
        ({
            productCode,
            score,
            trigger,
            clickSource,
            rowNumber,
            status,
            category,
            isBestMatchCard,
            isCounterOffer,
            isNewOffer,
        }: {
            category: CreditCardProductCategory
            clickSource: string
            isBestMatchCard: boolean
            isCounterOffer: boolean
            isNewOffer: boolean
            productCode: string
            rowNumber: number
            score: number
            status: string
            trigger: string
        }) => {
            gtmSend({
                event: 'cards-click-out',
                category,
                hasPassword: false,
            })

            send('PostClick', {
                customerId: appQueryState.customerId,
                customerType: appQueryState.customerType,
                isKnownCustomer: appQueryState.isKnownCustomer,
                visitOrigin: appQueryState.visitOrigin,
                score,
                status,
                isBestMatchCard,
                isCounterOffer,
                category,
                row: rowNumber,
                clicksource: clickSource,
                productcode: productCode,
                trigger,
                client: 'web',
                isDownsell: appQueryState.referrer === 'loansdownsell',
                googleClickId: trackingData.gclid,
                isNewOffer,
            })
        },
        [
            appQueryState.customerId,
            appQueryState.customerType,
            appQueryState.isKnownCustomer,
            appQueryState.referrer,
            appQueryState.visitOrigin,
            gtmSend,
            send,
        ]
    )

    const logAlternativeProductApplyEvent = useCallback(
        ({
            productCode,
            rowNumber,
            trigger,
            clickSource,
        }: {
            clickSource: string
            productCode: string
            rowNumber: number
            trigger: string
        }) => {
            send('PostClick', {
                productCode,
                row: rowNumber,
                score: 1,
                trigger,
                clicksource: clickSource,
                client: 'web',
                status: '',
                category: 'AP',
                isBestMatchCard: false,
                googleClickId: trackingData.gclid,
            })
        },
        [send]
    )

    const logSortChangeEvent = useCallback(
        ({ sortBy, category }: { category: string; sortBy: string }) => {
            send('Sort', {
                sortby: sortBy,
                category,
            })
        },
        [send]
    )

    const logCategoryChangeEvent = useCallback(
        ({ category }: { category: string }) => {
            send('CategoryChange', {
                category,
            })
        },
        [send]
    )

    const logAlternativeProductCategoryChangeEvent = useCallback(
        ({ categoryName }: { categoryName: string }) => {
            send('AlternativeProductCategoryClick', {
                customerId: appQueryState.customerId,
                categoryName,
            })
        },
        [appQueryState.customerId, send]
    )

    const logScoredTableUpdateEvent = useCallback(
        ({
            cards,
            minCreditLimit,
            category,
            sortedBy,
            seenNewOffers,
            numberOfNewOffersSeen,
        }: {
            cards?: ScoredCreditCardProduct[]
            category: CreditCardProductCategory
            minCreditLimit?: number | null
            numberOfNewOffersSeen: number
            seenNewOffers: boolean
            sortedBy: string
        }) => {
            if (cards == null) return
            const categoryCards = cards.map((card: ScoredCreditCardProduct) => {
                return {
                    productCode: card.productCode,
                    isSponsored: card.sponsored,
                    isDownsell: getCardIsDownsell(card),
                    guaranteedCreditLimit:
                        card.guaranteedRate?.guaranteedCreditLimit,
                    guaranteedOfferType:
                        card.guaranteedRate?.guaranteedOfferType,
                    isCounterOffer: card.guaranteedRate?.isCounterOffer,
                }
            })
            send('ScoredTableUpdate', {
                customerId: appQueryState.customerId,
                sortedProducts: categoryCards,
                minCreditLimit,
                category,
                sortedBy,
                seenNewOffers,
                numberOfNewOffersSeen,
            })
        },
        [send]
    )

    const logAppPromotionView = useCallback(
        ({ promotion }: { promotion: AppPromotionTypes }) => {
            send('AppPromotionView', {
                promotion,
                source: 'cardsProductTable',
                platform: navigator.userAgent,
            })
        },
        [send]
    )

    const logAppPromotionClick = useCallback(
        ({
            promotion,
            source,
        }: {
            promotion: AppPromotionTypes
            source:
                | 'cardsProductTable'
                | 'creditReport'
                | 'dismiss'
                | 'getTheApp'
                | 'modal'
        }) => {
            send('AppPromotionClick', {
                promotion,
                source,
                platform: navigator.userAgent,
            })
        },
        [send]
    )

    const logFeatureCardsViewEvent = useCallback(
        ({
            numberOfFeaturedCards,
            category,
        }: {
            category: CreditCardProductCategory
            numberOfFeaturedCards: number
        }) => {
            send('FeatureCards', {
                numberOfFeaturedCards,
                category,
            })
        },
        [send]
    )

    const logCreditLimitIntroView = useCallback(() => {
        send('CreditLimitIntroView', {
            customerId: appQueryState.customerId,
            client: 'web',
        })
    }, [send])

    const logMinCreditLimitSubmit = useCallback(
        ({
            minCreditLimit,
            source,
        }: {
            minCreditLimit: number
            source:
                | 'GuaranteedCreditLimitIntro'
                | 'creditCardsMinCreditLimitUpdateModal'
        }) => {
            send('MinCreditLimitSubmit', {
                customerId: appQueryState.customerId,
                minCreditLimit,
                source,
                client: 'web',
            })
        },
        [send]
    )

    const logMinCreditLimitSkip = useCallback(
        ({ source }: { source: 'GuaranteedCreditLimitIntro' }) => {
            send('MinCreditLimitSkip', {
                customerId: appQueryState.customerId,
                source,
                client: 'web',
            })
        },
        [send]
    )

    const logGuaranteedCreditLimitLanding = useCallback(() => {
        send('GuaranteedCreditLimitLanding', {
            customerId: appQueryState.customerId,
            scoreId: appQueryState.scoreId,
        })
    }, [send])

    const logClickOnNewOffersBanner = useCallback(
        ({ productCode }: BasinEvents['NewOffersBannerClick']) => {
            send('NewOffersBannerClick', { productCode })
        },
        [send]
    )

    const logEligibilityFactorsCardShownEvent = useCallback(
        ({
            numberOfRedEligibilityFactors,
        }: {
            numberOfRedEligibilityFactors: number
        }) => {
            send('EligibilityFactorsCardShown', {
                customerId: appQueryState.customerId,
                redEligibilityFactorsCount: numberOfRedEligibilityFactors,
            })
        },
        [send]
    )

    const logEligibilityFactorsCardDeepLinkClickEvent = useCallback(
        ({
            numberOfRedEligibilityFactors,
            route,
        }: {
            numberOfRedEligibilityFactors: number
            route: string
        }) => {
            send('EligibilityFactorsDeepLinkClick', {
                customerId: appQueryState.customerId,
                redEligibilityFactorsCount: numberOfRedEligibilityFactors,
                route,
            })
        },
        [send]
    )

    return {
        logAcquisitionPageFormOpenEvent,
        logAcquisitionPageFormStartEvent,
        logAcquisitionJourneyCardsTableViewEvent,
        logAcquisitionJourneyCardsTableClickoutEvent,

        logUnscoredPagesFormOpenEvent,

        logVisitEvent,
        logPageViewEvent,
        logCheckEligibilityEvent,
        logNavigateToCardPageEvent,
        logCardApplyEvent,
        logAlternativeProductApplyEvent,
        logCategoryChangeEvent,
        logSortChangeEvent,
        logAlternativeProductCategoryChangeEvent,
        logFeatureCardsViewEvent,
        logScoredTableUpdateEvent,

        logAppPromotionClick,
        logAppPromotionView,

        logCreditLimitIntroView,
        logMinCreditLimitSubmit,
        logMinCreditLimitSkip,
        logGuaranteedCreditLimitLanding,

        logClickOnNewOffersBanner,

        logEligibilityFactorsCardShownEvent,
        logEligibilityFactorsCardDeepLinkClickEvent,
    }
}

export default useLogEvents
